
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';
import { getDefaultDisplayVariant, DisplayVariantInfo } from '@/utils/variantSelection';

type Category = { id: string; name: string; };
type ProductRow = Database['public']['Tables']['products']['Row'];
export interface DependencyRule {
  pot_size: string;
  allowed_pot_types: string[];
  allowed_pot_colors: string[];
}
export type DbProduct = ProductRow & { 
  categories: Category[];
  category: string[];
  defaultVariant?: DisplayVariantInfo | null;
  dependency_rules?: DependencyRule[];
  product_variants?: (Database['public']['Tables']['product_variants']['Row'] & {
    variant_images?: Database['public']['Tables']['variant_images']['Row'][];
  })[];
};
type ProductWithRelations = ProductRow & {
  product_categories: { categories: { id: string; name: string; } | null }[];
  product_variants: (Database['public']['Tables']['product_variants']['Row'] & {
    variant_images: Database['public']['Tables']['variant_images']['Row'][];
  })[];
};

const fetchProductById = async (id: string): Promise<DbProduct | null> => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(id)) {
    console.error('Invalid UUID format:', id);
    throw new Error('Invalid product ID format');
  }
  
  const { data, error } = await supabase
    .from('products')
    .select<string, ProductWithRelations>(`
      *, 
      product_categories(categories(id, name)),
      product_variants(
        *,
        variant_images(*)
      )
    `)
    .eq('id', id)
    .eq('is_published', true)
    .maybeSingle();

  if (error) {
    console.error('Error fetching product:', error);
    throw new Error(error.message);
  }
  
  if (!data) {
    return null;
  }

  const { product_categories, product_variants, ...rest } = data;
  const categories = (product_categories || []).map(pc => pc.categories).filter(Boolean) as Category[];
  const categoryNames = categories.map(c => c.name);
  
  // Enhance variants with cover images from gallery
  const enhancedVariants = (product_variants || []).map(variant => {
    const coverImage = variant.variant_images?.find(img => img.is_cover);
    const sortedImages = [...(variant.variant_images || [])].sort((a, b) => 
      (a.sort_order || 0) - (b.sort_order || 0)
    );
    const firstImage = sortedImages[0];
    const galleryImageUrl = (coverImage || firstImage)?.image_url;
    
    return {
      ...variant,
      image_url: galleryImageUrl || variant.image_url
    };
  });
  
  const defaultVariant = getDefaultDisplayVariant(enhancedVariants);

  return { 
    ...rest, 
    categories, 
    category: categoryNames, 
    defaultVariant,
    dependency_rules: rest.dependency_rules as unknown as DependencyRule[] | undefined
  } as DbProduct;
};

const fetchRelatedProducts = async (categories: Category[] | null, currentProductId: string): Promise<DbProduct[]> => {
  const categoryIds = (categories || []).map(c => c.id);

  if (categoryIds.length === 0) {
    const { data: randomProducts, error } = await supabase
      .from('products')
      .select<string, ProductWithRelations>(`
        *, 
        product_categories(categories(id, name)),
        product_variants(*, variant_images(*))
      `)
      .not('id', 'eq', currentProductId)
      .eq('is_published', true)
      .limit(6);
    if (error) return [];
    const transformed = (randomProducts || []).map(p => {
        const { product_categories, product_variants, ...rest } = p;
        const fetchedCategories = (product_categories || []).map(pc => pc.categories).filter(Boolean) as Category[];
        const categoryNames = fetchedCategories.map(c => c.name);
        const defaultVariant = getDefaultDisplayVariant(product_variants || []);
        return { ...rest, categories: fetchedCategories, category: categoryNames, defaultVariant, product_variants };
    });
    return transformed as DbProduct[];
  }

  const { data: productCategories, error: pcError } = await supabase
    .from('product_categories')
    .select('product_id')
    .in('category_id', categoryIds)
    .neq('product_id', currentProductId);

  if (pcError) {
      console.error('Error fetching related product ids:', pcError.message);
      return [];
  }

  const relatedProductIds = [...new Set(productCategories.map(pc => pc.product_id))].slice(0, 6);

  if (relatedProductIds.length === 0) return [];

  const { data, error: productsError } = await supabase
    .from('products')
    .select<string, ProductWithRelations>(`
      *, 
      product_categories(categories(id, name)),
      product_variants(*, variant_images(*))
    `)
    .in('id', relatedProductIds)
    .eq('is_published', true);

  if(productsError) {
    console.error('Error fetching related product details:', productsError.message);
    return [];
  }

  // If no published products found in same categories, fall back to random products
  if (!data || data.length === 0) {
    const { data: randomProducts, error: fallbackError } = await supabase
      .from('products')
      .select<string, ProductWithRelations>(`
        *, 
        product_categories(categories(id, name)),
        product_variants(*, variant_images(*))
      `)
      .not('id', 'eq', currentProductId)
      .eq('is_published', true)
      .limit(6);
    
    if (fallbackError) {
      console.error('Error fetching fallback products:', fallbackError.message);
      return [];
    }
    
    const fallbackTransformed = (randomProducts || []).map(p => {
      const { product_categories, product_variants, ...rest } = p;
      const fetchedCategories = (product_categories || []).map(pc => pc.categories).filter(Boolean) as Category[];
      const categoryNames = fetchedCategories.map(c => c.name);
      const defaultVariant = getDefaultDisplayVariant(product_variants || []);
      return { ...rest, categories: fetchedCategories, category: categoryNames, defaultVariant, product_variants };
    });
    
    return fallbackTransformed as DbProduct[];
  }

  const transformed = (data || []).map(p => {
    const { product_categories, product_variants, ...rest } = p;
    const fetchedCategories = (product_categories || []).map(pc => pc.categories).filter(Boolean) as Category[];
    const categoryNames = fetchedCategories.map(c => c.name);
    const defaultVariant = getDefaultDisplayVariant(product_variants || []);
    return { ...rest, categories: fetchedCategories, category: categoryNames, defaultVariant, product_variants };
  });

  return transformed as DbProduct[];
};

export const useProduct = (id?: string) => {
  const { data: product, isLoading, error } = useQuery({
    queryKey: ['product', id],
    queryFn: () => {
      if (!id) return Promise.resolve(null);
      return fetchProductById(id);
    },
    enabled: !!id,
  });

  const { data: relatedProducts, isLoading: isLoadingRelated } = useQuery({
    queryKey: ['related-products', product?.id],
    queryFn: () => {
      if (!product || !product.id) return Promise.resolve([]);
      return fetchRelatedProducts(product.categories || null, product.id);
    },
    enabled: !!product,
  });

  return { product, isLoading, error, relatedProducts, isLoadingRelated };
};
